import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Settings2, Star, Trash2, Eye, Pencil, BarChart3 } from "lucide-react";

const inp = "h-9 px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]";
const PINK = "#C4738A";
const GRADE_CLS = { "Sangat Baik": "bg-emerald-50 text-emerald-600", "Baik": "bg-teal-50 text-teal-600", "Cukup": "bg-amber-50 text-amber-600", "Perlu Pembinaan": "bg-red-50 text-red-500" };

export function Stars({ n, size = "w-3.5 h-3.5" }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= Math.round(n) ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"}`} />)}
    </span>
  );
}

export const wibMonth = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 7);
export const monthLabel = (p) => p ? new Date(p + "-15").toLocaleDateString("id-ID", { month: "long", year: "numeric" }) : "-";

export default function AdminAppraisals() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [period, setPeriod] = useState(wibMonth());
  const [statusF, setStatusF] = useState("");
  const [aspects, setAspects] = useState([]);

  const load = useCallback(() => {
    setItems(null);
    const params = {};
    if (period) params.period = period;
    if (statusF) params.status = statusF;
    api.get("/admin/appraisals", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [period, statusF]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/appraisal-aspects").then((r) => setAspects(r.data)).catch(() => {}); }, []);

  const list = items || [];
  const done = list.filter((a) => a.status === "diajukan");
  const avg = done.length ? Math.round(done.reduce((s, a) => s + (a.final_score || 0), 0) / done.length * 10) / 10 : null;

  const del = async (a) => {
    if (!window.confirm(`Hapus draft penilaian ${a.employee_name} periode ${monthLabel(a.period)}?`)) return;
    try { await api.delete(`/admin/appraisals/${a.id}`); toast.success("Draft dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div data-testid="appraisal-page" className="space-y-5 pb-10">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Penilaian Karyawan</h1>
          <p className="text-xs text-slate-400">Evaluasi kinerja dan pengembangan karier — aspek penilaian bisa diatur sendiri.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="appr-aspect-btn" onClick={() => nav("/admin/settings/master?tab=aspek")} className="h-10 px-4 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-1.5 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">
            <Settings2 className="w-4 h-4" /> Atur Aspek
          </button>
          <button data-testid="appr-create-btn" onClick={() => nav("/admin/appraisals/new")} className="h-10 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors">
            <Plus className="w-4 h-4" /> Buat Penilaian
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl border border-slate-100 p-4"><div className="text-[10px] text-slate-400">Periode</div>
          <div className="font-display text-lg font-bold text-slate-900">{monthLabel(period) || "Semua"}</div></div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4"><div className="text-[10px] text-slate-400">Total Penilaian</div>
          <div data-testid="appr-stat-total" className="font-display text-lg font-bold text-slate-900">{list.length}</div></div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4"><div className="text-[10px] text-slate-400">Diajukan / Draft</div>
          <div className="font-display text-lg font-bold text-slate-900">{done.length} <span className="text-slate-300">/</span> {list.length - done.length}</div></div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4"><div className="text-[10px] text-slate-400">Rata-rata Nilai (diajukan)</div>
          <div data-testid="appr-stat-avg" className="font-display text-lg font-bold text-[#C4738A] flex items-center gap-2">{avg ?? "—"} {avg && <Stars n={avg} />}</div></div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-2 flex-wrap">
        <input data-testid="appr-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className={inp} />
        {[{ id: "", label: "Semua Status" }, { id: "draft", label: "Draft" }, { id: "diajukan", label: "Diajukan ke Owner" }].map((f) => (
          <button key={f.id} data-testid={`appr-filter-${f.id || "all"}`} onClick={() => setStatusF(f.id)}
            className={`h-8 px-4 rounded-full text-[11px] font-semibold border transition-colors ${statusF === f.id ? "border-[#C4738A] text-[#C4738A] bg-rose-50/50" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{f.label}</button>
        ))}
        <span className="text-[11px] text-slate-400 ml-auto flex items-center gap-1"><BarChart3 className="w-3.5 h-3.5" /> {aspects.length} aspek aktif • total bobot {aspects.reduce((s, a) => s + a.weight, 0)}%</span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Karyawan</th>
              <th className="text-left px-3 py-2.5 font-semibold">Periode</th>
              <th className="text-left px-3 py-2.5 font-semibold">Nilai Akhir</th>
              <th className="text-center px-3 py-2.5 font-semibold">Status</th>
              <th className="text-left px-3 py-2.5 font-semibold">Penilai</th>
              <th className="text-left px-3 py-2.5 font-semibold">Dibuat</th>
              <th className="px-3 py-2.5 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items === null && <tr><td colSpan={7} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {items?.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-xs text-slate-400">Belum ada penilaian pada periode ini. Klik "Buat Penilaian".</td></tr>}
            {list.map((a) => (
              <tr key={a.id} data-testid={`appr-row-${a.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                      {a.employee_photo && <img src={a.employee_photo} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div><div className="text-xs font-semibold text-slate-800">{a.employee_name}</div>
                      <div className="text-[10px] text-slate-400 capitalize">{(a.employee_role || "").replace(/_/g, " ")}</div></div>
                  </div>
                </td>
                <td className="px-3 py-3 text-xs text-slate-600">{monthLabel(a.period)}</td>
                <td className="px-3 py-3">
                  {a.final_score != null ? (
                    <div className="flex items-center gap-2">
                      <span className="font-display text-base font-bold text-slate-900">{String(a.final_score).replace(".", ",")}</span>
                      <Stars n={a.final_score} size="w-3 h-3" />
                      {a.grade && <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${GRADE_CLS[a.grade]}`}>{a.grade}</span>}
                    </div>
                  ) : <span className="text-[11px] text-slate-400">Belum dinilai</span>}
                </td>
                <td className="px-3 py-3 text-center">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${a.status === "diajukan" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{a.status === "diajukan" ? "Diajukan" : "Draft"}</span>
                </td>
                <td className="px-3 py-3 text-[11px] text-slate-500">{a.created_by}</td>
                <td className="px-3 py-3 text-[11px] text-slate-400">{formatDateID(a.created_at)}</td>
                <td className="px-3 py-3">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`appr-view-${a.id}`} onClick={() => nav(`/admin/appraisals/${a.id}`)} title={a.status === "draft" ? "Lanjutkan / Ubah" : "Lihat"}
                      className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors">
                      {a.status === "draft" ? <Pencil className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    {a.status === "draft" && (
                      <button data-testid={`appr-del-${a.id}`} onClick={() => del(a)} title="Hapus draft" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

